<script setup lang="ts">
import FileImage from '@/core/files/FileImage.vue';
import type { Components } from '@/api/generated/api';
import { AttachmentContentType } from 'common/enum/entry.attachment.enum';
import CloseIcon from '@/assets/icons/CloseIcon.vue';
import DocumentIcon from '@/assets/icons/DocumentIcon.vue';
import ExternalMaterialIcon from '@/assets/icons/ExternalMaterialIcon.vue';
import ImageIcon from '@/assets/icons/ImageIcon.vue';
import { computed, ref } from 'vue';
import { useFileUrl } from '@/composables/files/useFileUrl';
import { useToast } from '@/composables/useToast';

export interface IAttachmentCardItem {
  id: number;
  fileId?: number;
  type: Components.Schemas.AttachmentContentType;
  name: string;
  imageSource: number | string | null;
  url: string | null;
}

const props = withDefaults(
  defineProps<{
    item: IAttachmentCardItem;
    removable?: boolean;
    disabled?: boolean;
    previewOnly?: boolean;
    elevated?: boolean;
  }>(),
  { previewOnly: false },
);
const emit = defineEmits<{ remove: [] }>();
const imageFailed = ref(false);
const opening = ref(false);
const { open } = useFileUrl();
const { showError } = useToast();
const element = computed(() => {
  if (props.previewOnly) return 'div';
  return props.item.fileId == null ? 'a' : 'button';
});
const openFile = async (): Promise<void> => {
  if (props.previewOnly || props.item.fileId == null || opening.value) return;
  opening.value = true;
  try {
    await open(props.item.fileId, props.item.type === AttachmentContentType.Image);
  } catch (error) {
    showError(error);
  } finally {
    opening.value = false;
  }
};
</script>

<template>
  <!-- mobile flex row: up to two cards share it (half each, minus half the gap), from three on they stay 128px so it overflows; desktop sizes come from the grid columns -->
  <div
    class="group relative aspect-square shrink-0 grow basis-[128px] max-w-[calc(50%_-_8px)] overflow-hidden border-divider bg-surface-tertiary md:max-w-none"
    :class="elevated ? 'rounded-[16px] border-[0.5px] shadow-level-1' : 'rounded-[18px] border'"
    @click="!previewOnly && $event.stopPropagation()"
  >
    <component
      :is="element"
      :type="element === 'button' ? 'button' : undefined"
      :disabled="opening"
      :aria-busy="opening"
      :href="previewOnly ? undefined : (item.url ?? undefined)"
      :target="previewOnly ? undefined : '_blank'"
      :rel="previewOnly ? undefined : 'noopener noreferrer'"
      :tabindex="previewOnly ? undefined : 0"
      class="absolute inset-0 flex flex-col outline-inner"
      :aria-label="item.name"
      @click="openFile"
    >
      <div v-if="item.type === AttachmentContentType.Image && !imageFailed" class="min-h-0 flex-1">
        <FileImage
          :source="item.imageSource"
          :alt="item.name"
          loading="lazy"
          decoding="async"
          class="size-full object-cover"
          @error="imageFailed = true"
        />
      </div>
      <div
        v-else
        class="flex min-h-0 flex-1 items-center justify-center"
        :class="item.type === AttachmentContentType.App ? 'p-md' : 'px-md pt-md'"
      >
        <FileImage
          v-if="item.type === AttachmentContentType.App && item.imageSource && !imageFailed"
          :source="item.imageSource"
          alt=""
          loading="lazy"
          class="size-[96px] max-h-full max-w-full rounded-[14px] object-contain"
          @error="imageFailed = true"
        />
        <ExternalMaterialIcon v-else-if="item.type === AttachmentContentType.App" class="size-button text-secondary" />
        <div
          v-else
          class="flex size-full items-center justify-center rounded-t-[10px] bg-surface-primary text-tertiary"
        >
          <ImageIcon v-if="item.type === AttachmentContentType.Image" class="size-button" aria-hidden="true" />
          <DocumentIcon v-else class="size-button" aria-hidden="true" />
        </div>
      </div>
      <div
        v-if="item.type !== AttachmentContentType.Image"
        class="flex h-button shrink-0 items-center gap-sm bg-white px-md text-footer text-primary"
      >
        <ExternalMaterialIcon
          v-if="item.type === AttachmentContentType.App"
          class="size-icon-xs shrink-0 text-secondary"
        />
        <DocumentIcon v-else class="size-icon-xs shrink-0 text-secondary" aria-hidden="true" />
        <span class="truncate" :title="item.name">{{ item.name }}</span>
      </div>
      <div
        v-else
        class="absolute inset-x-0 bottom-0 hidden h-button items-center gap-sm bg-black/50 px-md text-footer text-white group-hover:flex group-focus-within:flex max-md:flex"
      >
        <ImageIcon class="size-icon-xs shrink-0" /><span class="truncate">{{ item.name }}</span>
      </div>
    </component>
    <button
      v-if="removable && !previewOnly"
      type="button"
      :disabled="disabled"
      class="absolute right-sm top-sm hidden size-icon-button items-center justify-center rounded-full bg-black/35 text-white transition-colors outline-inner hover:bg-black/55 active:bg-black/65 group-hover:flex group-focus-within:flex max-md:flex"
      :aria-label="$t('entryAttachment.removeNamed', { name: item.name })"
      @click.stop="emit('remove')"
    >
      <CloseIcon class="size-icon-sm" />
    </button>
    <slot />
  </div>
</template>
